import { SectionHeading } from '~/components/SectionHeading'
import type { LandingMonthGroup } from '~/api/types'
import { EventCard } from '~/components/EventCard'

import s from './JustAdded.module.css'

/**
 * The fourth section's name. It says the picks are recent, which is what the query means —
 * `decided_at`, when somebody chose it. "Just announced" would have claimed something about
 * the promoter instead, and a gig announced in March and picked yesterday belongs here all
 * the same. One constant, so it is one edit.
 */
const RECENT_HEADING = 'Freshly curated'

/** Where the rest of the picks are, which is the calendar filtered to them. */
const ALL_PICKS = '/calendar?view=picked&grid=list'

/**
 * Written out rather than read off a formatter. The server sends `"2026-08"` and nothing
 * else; naming the month here from a fixed table keeps it English on a reader whose machine
 * is not, which is what the Python's `_MONTHS` was for.
 */
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/** `"2026-08"` to `"August 2026"`, or the month back unchanged if it is not that shape. */
function monthLabel(month: string): string {
  const [year, index] = month.split('-')
  const name = MONTHS[Number(index) - 1]
  return name && year ? `${name} ${year}` : month
}

export interface JustAddedProps {
  /** Picked in the last seven days, grouped by the month they happen in. */
  months: LandingMonthGroup[]
  /** Whether more was withheld, never how much — a count would be a fact about the
   *  selection rather than about what is on. */
  hasMore: boolean
}

/**
 * Months as columns. Nothing to unfold — everything it holds is already on the page, which
 * is why this is the one section with no fold, a flat seam and a note that always shows.
 */
export function JustAdded({ months, hasMore }: JustAddedProps) {
  return (
    <section className={s.sec} id="fresh">
      <div className={s.head}>
        <SectionHeading>{RECENT_HEADING}</SectionHeading>
        <div className={s['head-r']}>
          <span className={`${s.meta} ${s.mono}`}>PICKED IN THE LAST SEVEN DAYS</span>
        </div>
      </div>

      <div className={s.months}>
        {months.map(group => (
          <div className={s.mgroup} key={group.month}>
            {/* The name is wrapped so it can carry the page's background and break the rule
                it sits on, rather than the rule running under the words. */}
            <h3 className={s.mono}>
              <span>{monthLabel(group.month)}</span>
            </h3>
            <ol className={s.stack}>
              {group.events.map(event => (
                // The column is already headed by its month, so the card's date drops it.
                <EventCard key={event.id} event={event} withMonth={false} variant="stack" />
              ))}
            </ol>
          </div>
        ))}
      </div>

      <div className={`${s.seam} ${s.flat}`}>
        {hasMore ? (
          // A plain anchor, not a Link: the calendar is still served by Python and a client
          // navigation would land on a route that does not exist yet.
          <a className={`${s['seam-note']} ${s.more} ${s.mono}`} href={ALL_PICKS}>
            the calendar has the rest{' '}
            <span className={s.arrow} aria-hidden="true">
              &#8594;
            </span>
          </a>
        ) : (
          <span className={`${s['seam-note']} ${s.done} ${s.mono}`}>
            that is every {RECENT_HEADING.toLowerCase()}
          </span>
        )}
      </div>
    </section>
  )
}
